import React, { useState, useEffect, useCallback } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import AnalogClock from './AnalogClock';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import './ClockBuilder.css';

const EXERCISE_DATA = [
  // Базовые
  { id: 1, hours: 3, minutes: 0, sayText: 'Поставь на часах три часа' },
  { id: 2, hours: 5, minutes: 0, sayText: 'Поставь на часах пять часов' },
  { id: 3, hours: 4, minutes: 30, sayText: 'Поставь на часах четыре тридцать' },
  
  // ХИТРЫЕ ЗАДАНИЯ (Разговорные выражения)
  { id: 4, hours: 6, minutes: 30, sayText: 'Поставь на часах пол седьмого' },
  { id: 5, hours: 2, minutes: 15, sayText: 'Поставь на часах четверть третьего' },
  { id: 6, hours: 7, minutes: 45, sayText: 'Поставь на часах без четверти восемь' },
  { id: 7, hours: 9, minutes: 15, sayText: 'Поставь на часах четверть десятого' },
  { id: 8, hours: 11, minutes: 30, sayText: 'Поставь на часах половина двенадцатого' },
];

const formatTime = (h, m) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

const ClockBuilder = ({ ageGroup, onBack, onComplete }) => {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [feedback, setFeedback] = useState(null); // 'correct' | 'wrong' | null

  // Текущее положение стрелок, которые настраивает ребенок
  const [userHours, setUserHours] = useState(12);
  const [userMinutes, setUserMinutes] = useState(0);

  const { play: playInstructionAudio, stop: stopInstructionAudio } = useInstructionAudio();

  useEffect(() => {
    const shuffled = [...EXERCISE_DATA].sort(() => Math.random() - 0.5);
    setQuestions(shuffled);
  }, []);

  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;

  const stopAll = () => {
    stopInstructionAudio();
  };

  const goToNextQuestion = () => {
    setCurrentIndex(prev => {
      if (prev < totalQuestions - 1) return prev + 1;
      setShowResult(true);
      return prev;
    });
  };

  const finishAnswer = (isCorrect) => {
    if (isProcessing) return;
    stopAll();

    setIsSpeaking(false);
    setIsListening(false);
    setFeedback(isCorrect ? 'correct' : 'wrong');
    setAnswers(prev => [...prev, isCorrect]);

    setTimeout(() => {
      setFeedback(null);
      setIsProcessing(false);
      setUserHours(12);
      setUserMinutes(0);
      goToNextQuestion();
    }, 1500);
  };

  const checkAnswer = () => {
    if (!currentQuestion || isProcessing) return;
    setIsProcessing(true);
    const isCorrect = userHours === currentQuestion.hours && userMinutes === currentQuestion.minutes;
    finishAnswer(isCorrect);
  };

  const processQuestion = useCallback(() => {
    if (!currentQuestion || showResult) return;
    stopAll();

    setIsListening(true);
    setIsSpeaking(false);
    setFeedback(null);

    const beginInteraction = () => {
      setIsListening(false);
      setIsSpeaking(true);
    };

    playInstructionAudio(currentQuestion.sayText, beginInteraction);
  }, [currentQuestion, showResult, playInstructionAudio]);

  // Функция повтора инструкции
  const repeatInstruction = useCallback(() => {
    if (isProcessing || feedback || !currentQuestion) return;
    playInstructionAudio(currentQuestion.sayText);
  }, [isProcessing, feedback, currentQuestion, playInstructionAudio]);

  useEffect(() => {
    if (currentQuestion) {
      processQuestion();
    }
  }, [currentIndex, processQuestion]);

  useEffect(() => {
    return () => stopAll();
  }, []);

  // Передача результатов
  useEffect(() => {
    if (!showResult) return;
    const timer = setTimeout(() => {
      const correctCount = answers.filter(a => a === true).length;
      const score = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
      if (onComplete) onComplete({ testId: 'ClockBuilder', score });
      else onBack();
    }, 3000);
    return () => clearTimeout(timer);
  }, [showResult, answers, totalQuestions, onComplete, onBack]);

  // Управление кнопками
  const changeHours = (delta) => {
    if (isProcessing) return;
    setUserHours(prev => {
      let next = prev + delta;
      if (next > 12) next = 1;
      if (next < 1) next = 12;
      return next;
    });
  };

  const changeMinutes = (delta) => {
    if (isProcessing) return;
    setUserMinutes(prev => {
      let next = prev + delta;
      if (next >= 60) next = 0;
      if (next < 0) next = 55;
      return next;
    });
  };

  // === РЕНДЕР ===
  if (showResult) {
    const correctCount = answers.filter(a => a === true).length;
    return (
      <ModuleTemplate progress={answers} totalQuestions={totalQuestions} forceStop={true} onComplete={onComplete || onBack}>
        <div className="cb-result-content">
          <h2 className="cb-result-title">🎉 Упражнение завершено!</h2>
          <div className="cb-result-score">
            <span className="cb-score-number">{correctCount}</span>
            <span className="cb-score-total"> / {totalQuestions}</span>
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  if (!currentQuestion) {
    return (
      <ModuleTemplate progress={[]} totalQuestions={1} forceStop={true} onComplete={onComplete || onBack}>
        <div className="cb-loading-text">Загрузка...</div>
      </ModuleTemplate>
    );
  }

  return (
    <ModuleTemplate 
      progress={answers} 
      totalQuestions={totalQuestions} 
      forceStop={true} 
      onComplete={onComplete || onBack}
      disabled={isProcessing}
      onRepeat={repeatInstruction}
      extraContent={currentQuestion.sayText}
    >
      <div className="cb-game-area">
        
        {/* Главный ряд: Кнопки слева, часы по центру, кнопки справа */}
        <div className="cb-middle-row">
          
          {/* ЛЕВАЯ КОЛОНКА - УПРАВЛЕНИЕ ЧАСАМИ (КРАСНЫЕ) */}
          <div className="cb-control-group">
            <button className="cb-ctrl-btn red-btn" onClick={() => changeHours(1)}>+</button>
            <span className="cb-ctrl-label red-label">Час</span>
            <button className="cb-ctrl-btn red-btn" onClick={() => changeHours(-1)}>−</button>
          </div>

          {/* ЦЕНТР - ЧАСЫ (БЕЗ ЦИФРОВОГО ТАБЛО!) */}
          <div className="cb-clock-display-wrapper">
            {/* Индикаторы помещаем прямо над часами */}
            <div className="cb-indicators">
              <div className={`cb-indicator listen ${isListening ? 'active' : ''}`}>
                <span>🎧</span>
              </div>
              <div className={`cb-indicator speak ${isSpeaking ? 'active' : ''}`}>
                <span>🗣️</span>
              </div>
            </div>

            <div className={`cb-clock-frame ${feedback ? feedback : ''}`}>
              <AnalogClock hours={userHours} minutes={userMinutes} size={260} />
            </div>
          </div>

          {/* ПРАВАЯ КОЛОНКА - УПРАВЛЕНИЕ МИНУТАМИ (СИНИЕ) */}
          <div className="cb-control-group">
            <button className="cb-ctrl-btn blue-btn" onClick={() => changeMinutes(5)}>+</button>
            <span className="cb-ctrl-label blue-label">Минуты</span>
            <button className="cb-ctrl-btn blue-btn" onClick={() => changeMinutes(-5)}>−</button>
          </div>

        </div>

        {/* Кнопка проверки внизу */}
        <button 
          className={`cb-check-btn ${feedback ? feedback : ''}`} 
          onClick={checkAnswer} 
          disabled={isProcessing}
        >
          {feedback === 'wrong' ? 'Ошибка!' : feedback === 'correct' ? 'Верно!' : 'Проверить'}
        </button>

      </div>
    </ModuleTemplate>
  );
};

export default ClockBuilder;